import { Alert, Platform } from 'react-native';
import { router } from 'expo-router';
import { useAppStore } from '../store/useAppStore';
import type { UserRole } from '../types/roles';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MODO VISTA PREVIA — explorar la app sin cuenta
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * QUÉ ES
 * Cualquier persona puede recorrer la app completa, en el rol que quiera,
 * sin registrarse. Ve las pantallas reales; lo único que no puede es
 * EJECUTAR acciones (pedir un viaje, cobrar, ponerse en línea).
 *
 * POR QUÉ EXISTE
 * Dos razones, y las dos importan:
 *   1. Producto: quitar el muro de registro sube la conversión. Nadie crea
 *      una cuenta para ver si una app le sirve.
 *   2. Play Store: si el revisor no puede entrar, rechaza. Ya nos pasó —
 *      la cuenta está suspendida justamente por eso.
 *
 * REGLA INNEGOCIABLE
 * Es una función PÚBLICA, para todos. Nunca un modo oculto que se active al
 * detectar a un revisor de Google: eso es motivo de expulsión, no de
 * aprobación. Ver el comentario en `featureFlags.ts` sobre LOGIN_ENFORCED_FROM.
 *
 * PRINCIPIO DE REPARTO
 * Se bloquean las ACCIONES, nunca la NAVEGACIÓN.
 */

/**
 * ¿El usuario está explorando sin cuenta?
 *
 * Doble llave a propósito: la bandera encendida Y la ausencia de sesión real.
 * Si alguien se registra estando en vista previa y por lo que sea la bandera
 * no se apagó, la presencia del uid manda — nunca tratamos como invitado a
 * alguien que sí tiene cuenta.
 */
export function esInvitado(): boolean {
  const s = useAppStore.getState();
  return s.guestPreview === true && !s.userFirebaseUid;
}

/**
 * Puerta única para acciones que exigen cuenta.
 *
 * Devuelve `true` si BLOQUEÓ (el caller debe cortar) y `false` si puede seguir.
 *
 * Sustituye a los `if (!uid) return;` mudos que había repartidos por la app:
 * el usuario tocaba un botón, no pasaba absolutamente nada, y eso es
 * literalmente lo que Google reportó como "Unresponsive UI elements".
 * Aquí siempre hay respuesta visible y una salida clara.
 *
 * @param accion  Verbo en infinitivo que completa la frase
 *                "Crea tu cuenta para {accion}".
 *                Ej: 'pedir un viaje', 'conectarte y recibir viajes'.
 */
export function bloquearSiInvitado(accion: string): boolean {
  if (!esInvitado()) return false;

  const titulo = 'Estás explorando sin cuenta';
  const cuerpo = `Puedes ver toda la app libremente. Para ${accion} necesitas una cuenta — es gratis y toma un minuto.`;

  // `Alert.alert` NO EXISTE en react-native-web: es un no-op silencioso.
  // Comprobado en el navegador — el invitado tocaba "Conectarse" y no pasaba
  // absolutamente nada. Es decir, esta misma función, creada para eliminar los
  // botones mudos, era un botón mudo en web. Ahí usamos el diálogo del
  // navegador, que es lo que ya hace el resto de la app (ver `profile.tsx`).
  if (Platform.OS === 'web') {
    if (typeof window !== 'undefined' && window.confirm(`${titulo}\n\n${cuerpo}\n\n¿Crear tu cuenta ahora?`)) {
      router.push('/auth');
    }
    return true;
  }

  Alert.alert(titulo, cuerpo, [
    { text: 'Seguir explorando', style: 'cancel' },
    { text: 'Crear mi cuenta', onPress: () => router.push('/auth') },
  ]);
  return true;
}

/**
 * A dónde entra el invitado según el rol que eligió explorar.
 *
 * IMPORTANTE: ninguna ruta apunta al embudo de KYC. Sin cuenta no hay KYC
 * posible, así que mandarlo ahí lo dejaría atrapado en un callejón sin
 * salida — exactamente el bucle por el que Google rechazó la versión 16.
 * El invitado va DIRECTO al panel del rol.
 */
export const RUTA_INVITADO_POR_ROL: Record<string, string> = {
  PASSENGER: '/(tabs)/map',
  DRIVER: '/(tabs)/map',
  DELIVERY_DRIVER: '/(tabs)/map',
  SCHOOL_DRIVER: '/(tabs)/map',
  FOOD_DELIVERY: '/(tabs)/food',
  RESTAURANT_OWNER: '/restaurant-dashboard',
  RENT_CAR_OWNER: '/(tabs)/dashboard',
  CAR_RENTER: '/(tabs)/marketplace',
  SCHOOL_DIRECTOR: '/school-director-dashboard',
};

/** Ruta de entrada para un rol; cae al mapa si el rol no está mapeado. */
export function rutaInvitadoPara(role: UserRole | null | undefined): string {
  return RUTA_INVITADO_POR_ROL[String(role ?? '')] ?? '/(tabs)/map';
}

/**
 * Enciende la vista previa y entra al rol elegido.
 * Se llama desde el selector de la portada.
 */
export function entrarComoInvitado(role: UserRole): void {
  const s = useAppStore.getState();
  s.setRole(role);
  s.setBaseRole(role);
  s.setGuestPreview(true);
  router.replace(rutaInvitadoPara(role) as any);
}

/** Sale de la vista previa y vuelve a la portada. */
export function salirDelModoInvitado(): void {
  const s = useAppStore.getState();
  s.setGuestPreview(false);
  s.setRole(null);
  s.setBaseRole(null);
  router.replace('/');
}
